import {
    Card,
    CardBody,
    Button,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    CardHeader,
    CardFooter,
    Input,
} from "@heroui/react";
import { LuPencil, LuTrash, LuPlus } from "react-icons/lu";
import { Link } from "react-router-dom";
import { useDataTable } from "@/hooks";
import { EmptyState, ErrorState, PageHeader } from "@/components/ui";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTablePagination,
    DataTableSkeleton,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import { FilterData } from "./components";
import { useUser } from "@/context/UserContext";

// Initial filters
const initialFilters = {
    name: "",
};

// Filter labels for chips display
const filterLabels = {
    name: { label: "Nama" },
};

const ProductUnitData = () => {
    const { hasPermission } = useUser();
    const hasAnyAction = hasPermission("material.edit") || hasPermission("material.delete");
    const table = useDataTable(
        "material-units/data",
        "material-units/{id}/delete",
        {},
        initialFilters,
    );

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description={table.error.message}
                onAction={table.refetch}
            />
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama Satuan", skeletonWidth: "w-32" },
        { name: "Simbol", skeletonWidth: "w-20" },
        ...(hasAnyAction ? [{ name: "Aksi", align: "end", skeletonWidth: "w-16" }] : []),
    ];

    const columns = [
        { key: "no", label: "No." },
        { key: "name", label: "Nama Satuan" },
        { key: "symbol", label: "Simbol" },
        ...(hasAnyAction
            ? [{ key: "actions", label: "Aksi", align: "end" }]
            : []),
    ];

    const renderCell = (item, columnKey) => {
        switch (columnKey) {
            case "no":
                return (
                    <>
                        {(table.pagination.page - 1) *
                            table.pagination.perPage +
                            table.items.indexOf(item) +
                            1}
                        .
                    </>
                );
            case "name":
                return item.name;
            case "symbol":
                return item.symbol || "-";
            case "actions":
                return (
                    <div className="relative flex items-center justify-end gap-2">
                        {hasPermission("material.edit") && (
                            <Tooltip content="Edit Satuan">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    as={Link}
                                    to={`/products-unit/${item.id}/edit`}
                                >
                                    <LuPencil
                                        size={14}
                                        className="text-default-500"
                                    />
                                </Button>
                            </Tooltip>
                        )}
                        {hasPermission("material.delete") && (
                            <Tooltip content="Hapus Satuan">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    color="danger"
                                    variant="light"
                                    onPress={() =>
                                        table.deleteState.openDeleteModal(
                                            item.id,
                                        )
                                    }
                                >
                                    <LuTrash size={14} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Page Header - Title/Breadcrumbs left, Actions right */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Satuan Produk"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produk", href: "/products" },
                        { label: "Satuan" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("material.create") && (
                        <Button
                            as={Link}
                            to="/products-unit/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Satuan
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData table={table} initialFilters={initialFilters} />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="flex flex-col gap-4">
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada satuan yang cocok dengan filter."
                                    : "Belum ada data satuan produk."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Satuan Produk" shadow="none">
                            <TableHeader columns={columns}>
                                {(column) => (
                                    <TableColumn
                                        key={column.key}
                                        align={column.align}
                                    >
                                        {column.label}
                                    </TableColumn>
                                )}
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {(columnKey) => (
                                            <TableCell>
                                                {renderCell(item, columnKey)}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />
        </div>
    );
};

export default ProductUnitData;
